import { setupTestErrorLogger } from "@/utils/errorLogger";
setupTestErrorLogger();

import React from "react";
import { createRoot } from "react-dom/client";

import * as Signals from "@/signals";
import * as Sentry from "@sentry/react";
import axios from "axios";
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType,
} from "react-router";

import { PagePreloading } from "./routes/preloading/PagePreloading";
import { createAppRoutes } from "./routes";
import { ThemeProvider } from "@/contexts/ThemeContext";

import Api from "@/api";
import { startAnalytics } from "@/analytics";
import AdminApi from "@/ee/admin_api";
import "./i18n";

import "@/api/socket";
import { ToasterBar } from "turboui";
import { installSentryAxiosInterceptor } from "@/utils/axiosErrorReporting";
import { configureSentryUser } from "@/utils/sentryContext";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/api/queryClient";

Api.default.setBasePath("/api/v2");
AdminApi.default.setBasePath("/admin/api/v1");

Signals.init();

if (window.appConfig.sentry.enabled) {
  Sentry.init({
    dsn: window.appConfig.sentry.dsn,
    release: window.appConfig.version,
    environment: window.appConfig.environment,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: 0,
  });

  configureSentryUser();
  installSentryAxiosInterceptor(axios);
}

const createRouter = window.appConfig.sentry.enabled
  ? Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)
  : createBrowserRouter;
const routes = createAppRoutes(createRouter);
startAnalytics(routes);

const rootElement: HTMLElement | null = document.getElementById("root");

const App: JSX.Element = (
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToasterBar />
      <ThemeProvider>
        <PagePreloading router={routes}>
          <RouterProvider router={routes} />
        </PagePreloading>
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>
);

if (rootElement !== null) {
  createRoot(rootElement).render(App);
} else {
  console.error("Root element not found");
}
